import React from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Control, Controller } from "react-hook-form";
import "./datepicker-custom.css";
import {
  Box,
  IconButton,
  InputAdornment,
  TextField,
  TextFieldProps,
  useTheme,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { tokens } from "../../theme";
import { useModalStyles } from "./useModalStyles";

type MonthYearPickerInputProps = TextFieldProps & {
  label?: string;
  isDisabled?: boolean;
};

const MonthYearPickerInput = React.forwardRef<HTMLInputElement, MonthYearPickerInputProps>(
  ({ label, isDisabled, value, InputLabelProps, InputProps, sx, ...props }, ref) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const displayValue = value ?? "";

    return (
      <TextField
        {...props}
        inputRef={ref}
        value={displayValue}
        label={label || ""}
        fullWidth
        type="text"
        disabled={isDisabled}
        color={theme.palette.mode === "dark" ? "secondary" : "primary"}
        variant="outlined"
        InputLabelProps={{
          shrink: Boolean(displayValue),
          ...InputLabelProps,
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton>
                <CalendarTodayIcon />
              </IconButton>
            </InputAdornment>
          ),
          ...InputProps,
        }}
        sx={[
          {
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: theme.palette.mode === "dark" ? "" : "#14E2E2",
              },
              "&.Mui-disabled": {
                "& fieldset": {
                  borderColor:
                    isDisabled && theme.palette.mode === "light" ? "#14E2E2" : "",
                },
                "& input": {
                  color:
                    isDisabled && theme.palette.mode === "light"
                      ? colors.grey[300]
                      : "",
                },
              },
            },
            "& .MuiInputBase-input.Mui-disabled": {
              WebkitTextFillColor:
                isDisabled && theme.palette.mode === "light" ? colors.grey[300] : "",
            },
            "& .MuiInputLabel-outlined": {
              color: theme.palette.mode === "dark" ? "" : colors.primary[100],
              "&.Mui-disabled": {
                color:
                  isDisabled && theme.palette.mode === "light" ? colors.grey[300] : "",
              },
            },
          },
          ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        ]}
      />
    );
  }
);

MonthYearPickerInput.displayName = "MonthYearPickerInput";

interface CustomMonthYearPickerProps {
  control: Control<any>;
  label?: string;
  controllerName: string;
  rules?: object;
  onDateChange?: (date: Date | null, endDate?: Date | null, isDateRange?: boolean | false) => void;
  isDisabled?: boolean;
  name?: string;
  isRange?: boolean;
  onClear?: () => void;
}

const MonthYearPicker: React.FC<CustomMonthYearPickerProps> = ({
  label,
  control,
  controllerName,
  rules,
  onDateChange,
  isDisabled,
  name,
  isRange,
  onClear,
  ...props
}) => {
  const theme = useTheme();
  const { tokens: mTokens, isDark } = useModalStyles();

  const datePickerStyles = {
    ".react-datepicker": {
      backgroundColor: `${mTokens.bg} !important`,
      border: `1px solid ${mTokens.border}`,
      borderRadius: "16px",
      overflow: "hidden",
      padding: "4px 0 10px",
    },
    ".react-datepicker__triangle": {
      display: "none",
    },
    ".react-datepicker__month-container": {
      backgroundColor: `${mTokens.bg} !important`,
      width: "100%",
    },
    ".react-datepicker__header": {
      backgroundColor: `${mTokens.bg} !important`,
      borderBottom: `1px solid ${mTokens.border}`,
      padding: "12px 10px 14px",
    },
    ".react-datepicker-year-header": {
      color: mTokens.textPrimary,
      fontWeight: 800,
      fontSize: "0.95rem",
    },
    ".react-datepicker__navigation": {
      color: mTokens.textSecondary,
    },
    ".react-datepicker__month": {
      padding: "12px 10px 4px",
    },
    ".react-datepicker__month-text": {
      color: mTokens.textPrimary,
      backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "rgba(15,23,42,0.02)",
    },
  };

  return (
    <>
      {isRange ? (
        <Box sx={datePickerStyles}>
          <Controller
            name={controllerName || ""}
            control={control}
            rules={rules ? rules : {}}
            render={({ field }) => {
              const fieldStartDate = field.value && Array.isArray(field.value) ? field.value[0] : null;
              const fieldEndDate = field.value && Array.isArray(field.value) ? field.value[1] : null;

              return (
                <DatePicker
                  selectsRange={true}
                  startDate={fieldStartDate || undefined}
                  endDate={fieldEndDate || undefined}
                  onChange={(update: [Date | null, Date | null]) => {
                    field.onChange(update);
                    if (onDateChange) {
                      onDateChange(update[0], update[1], true);
                    }
                    
                    if (!update[0] && !update[1] && onClear) {
                      onClear();
                      
                    }
                  }}
                  showMonthYearPicker
                  dateFormat="MMM yy"
                  isClearable={true}
                  customInput={
                    <MonthYearPickerInput
                      id={field.name}
                      name={name ? name : controllerName}
                      label={label || ""}
                      isDisabled={isDisabled}
                      {...props}
                    />
                  }
                />
              );
            }}
          />
        </Box>
      ) : (
        <Box sx={datePickerStyles}>
          <Controller
            name={controllerName || ""}
            control={control}
            rules={rules ? rules : {}}
            render={({ field }) => (
              <DatePicker
                selected={field.value ? new Date(field.value) : null}
                onChange={(date: Date | null) => {
                  field.onChange(date);
                  if (onDateChange) {
                    onDateChange(date);
                  }
                  if (!date && onClear) {
                    onClear();
                  }
                }}
                showMonthYearPicker
                dateFormat="MMM yy"
                isClearable={true}
                customInput={
                  <MonthYearPickerInput
                    id={field.name}
                    name={name ? name : controllerName}
                    label={label || ""}
                    isDisabled={isDisabled}
                    {...props}
                  />
                }
              />
            )}
          />
        </Box>
      )}
    </>
  );
};

export default MonthYearPicker;
